import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Lock, LogOut, Mail, CalendarClock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import SaasBilling from "@/pages/SaasBilling";

// Server-side trial state from /auth/me; flips to expired the moment any API returns 402 TRIAL_EXPIRED.
export function useTrialExpired() {
  const { user, refreshMe } = useAuth();
  const [hit, setHit] = useState(false);
  useEffect(() => {
    const on = () => { setHit(true); refreshMe?.(); };
    window.addEventListener("trial-expired", on);
    return () => window.removeEventListener("trial-expired", on);
  }, [refreshMe]);
  if (user?.trial?.status === "converted" || user?.trial?.status === "none") return false;
  return user?.trial?.status === "expired" || hit;
}

export function TrialExpiredScreen() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const canPay = hasPerm(user, "settings.edit");
  if (canPay && (loc.pathname === "/settings/billing" || loc.pathname === "/billing")) {
    return (
      <div className="min-h-screen bg-slate-50 p-4 sm:p-8" data-testid="trial-expired-billing">
        <div className="mx-auto max-w-5xl">
          <div className="mb-4 flex items-center justify-between rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            <span className="flex items-center gap-2"><Lock className="h-4 w-4" /> Your free trial has expired. Access returns as soon as your payment is verified.</span>
            <button data-testid="trial-expired-logout" onClick={logout} className="flex items-center gap-1 text-rose-700 hover:underline"><LogOut className="h-4 w-4" /> Log out</button>
          </div>
          <SaasBilling />
        </div>
      </div>
    );
  }
  return (
    <div className="grid min-h-screen place-items-center bg-slate-950 p-6 text-slate-100" data-testid="trial-expired-screen">
      <div className="w-full max-w-xl rounded-2xl border border-slate-800 bg-slate-900 p-8 shadow-2xl">
        <div className="mb-5 grid h-12 w-12 place-items-center rounded-xl bg-rose-500/15 text-rose-400"><Lock className="h-6 w-6" /></div>
        <h1 className="font-display text-3xl font-bold sm:text-4xl" data-testid="trial-expired-title">Your Pawkt free trial has expired</h1>
        <p className="mt-3 text-slate-300">Your 30-day free trial is complete. Upgrade to a paid plan to continue using Pawkt. Your business data will remain saved.</p>
        {!canPay && <p className="mt-3 text-sm text-amber-300" data-testid="trial-expired-ask-owner">Ask your business owner or administrator to upgrade the plan.</p>}
        <div className="mt-7 flex flex-wrap gap-3">
          {canPay && <Button data-testid="trial-view-plans" variant="outline" className="border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800" onClick={() => nav("/settings/billing#plans")}>View Plans</Button>}
          {canPay && <Button data-testid="trial-upgrade-now" className="bg-emerald-500 text-slate-950 hover:bg-emerald-400" onClick={() => nav("/settings/billing#plans")}>Upgrade Now</Button>}
          <a href="mailto:support@pawkt.in" data-testid="trial-contact-support"><Button variant="outline" className="border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800"><Mail className="mr-2 h-4 w-4" /> Contact Support</Button></a>
        </div>
        <button data-testid="trial-expired-logout-main" onClick={logout} className="mt-6 flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200"><LogOut className="h-4 w-4" /> Log out</button>
      </div>
    </div>
  );
}

export function TrialCountdown() {
  const { user } = useAuth();
  const live = useQuery({ queryKey: ["trial-status"], refetchInterval: 10 * 60 * 1000, refetchOnWindowFocus: true,
    enabled: user?.trial?.status === "active", queryFn: async () => (await api.get("/trial/status")).data });
  const t = live.data?.trial || user?.trial;
  if (!t || t.status !== "active") return null;
  const n = t.days_left;
  const tone = { info: "border-emerald-200 bg-emerald-50 text-emerald-900", warning: "border-amber-300 bg-amber-50 text-amber-900",
    critical: "border-rose-300 bg-rose-50 text-rose-900", final: "border-rose-500 bg-rose-600 text-white" }[t.level] || "";
  const msg = t.level === "final" ? "Your free plan expires today. Upgrade now to continue using Pawkt."
    : `Free plan expires in: ${n} day${n === 1 ? "" : "s"}`;
  const sub = t.level === "critical" ? "Your access will stop when the trial ends. Upgrade to keep using Pawkt without interruption."
    : t.level === "warning" ? "Less than a week left. Upgrade to keep all your data and features." : "";
  return (
    <div className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 ${tone}`} data-testid="trial-countdown" data-level={t.level}>
      <div className="flex items-start gap-2">
        {t.level === "info" ? <CalendarClock className="mt-0.5 h-4 w-4" /> : <AlertTriangle className="mt-0.5 h-4 w-4" />}
        <div><div className="font-semibold" data-testid="trial-countdown-text">{msg}</div>{sub && <div className="text-xs opacity-90">{sub}</div>}</div>
      </div>
      {hasPerm(user, "settings.edit") && t.level !== "info" && (
        <a href="/settings/billing#plans" data-testid="trial-countdown-upgrade"><Button size="sm" className={t.level === "final" ? "bg-white text-rose-700 hover:bg-rose-50" : "bg-slate-900 text-white"}>Upgrade now</Button></a>
      )}
    </div>
  );
}
